import type { ChunkMetadata, SearchResult } from "../types/citations";

/**
 * Check if a chunk represents an image source (VLM-extracted image from Epic OCR).
 * Image sources have source_type === "epic_ocr_image" and a valid image_artifact_id.
 */
export function isImageSource(metadata?: ChunkMetadata | null): boolean {
  if (!metadata) return false;
  return (
    metadata.source_type === "epic_ocr_image" &&
    typeof metadata.image_artifact_id === "number" &&
    metadata.image_artifact_id > 0
  );
}

/**
 * Get the image artifact ID from chunk metadata if it's an image source.
 * Returns null if not an image source.
 */
export function getImageArtifactId(
  metadata?: ChunkMetadata | null,
): number | null {
  if (!isImageSource(metadata)) return null;
  return metadata!.image_artifact_id!;
}

/**
 * Check if a SearchResult represents an image source.
 */
export function isImageSourceResult(result?: SearchResult | null): boolean {
  return isImageSource(result?.data_chunk?.chunk_metadata);
}

/**
 * Get the image artifact ID from a SearchResult if it's an image source.
 */
export function getImageArtifactIdFromResult(
  result?: SearchResult | null,
): number | null {
  return getImageArtifactId(result?.data_chunk?.chunk_metadata);
}

/**
 * Format a page number (or array of page numbers) for display.
 */
export function formatPageNumber(pn?: number | number[]): string {
  if (!pn) return "";
  if (Array.isArray(pn)) return pn.join(", ");
  return String(pn);
}

/**
 * Get a human-readable label for the image content type.
 */
export function getImageContentTypeLabel(contentType?: string | null): string {
  if (!contentType) return "Image";

  const labels: Record<string, string> = {
    chart: "Chart",
    diagram: "Diagram",
    table: "Table",
    photo: "Photo",
    figure: "Figure",
  };

  return labels[contentType.toLowerCase()] || "Image";
}
